/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleGenAI, LiveServerMessage, Modality, Session } from '@google/genai';
import { createBlob, decode, decodeAudioData } from '../utils/audio';
import { GEMINI_SYSTEM_INSTRUCTION } from '../constants/systemInstructions';
import { API_BASE_URL } from '../services/api';

// Fallback only. The authoritative model comes from the backend token response
// (it must match the model the ephemeral token is constrained to).
const DEFAULT_LIVE_MODEL = 'gemini-2.5-flash-native-audio-latest';

/** Give up on the WebSocket handshake after this long (ms). */
export const LIVE_CONNECT_TIMEOUT_MS = 15_000;

/** Error thrown by token fetchers; carries the HTTP status when known. */
export class LiveHelpTokenError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'LiveHelpTokenError';
    this.status = status;
  }
}

/**
 * Fetch a short-lived ephemeral auth token from our backend. The raw Gemini
 * key never reaches the browser — the server mints a token scoped to a single
 * Live session that expires within minutes.
 */
export async function fetchEphemeralToken(): Promise<{ token: string; model: string }> {
  const res = await fetch(`${API_BASE_URL}/api/live-help/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    throw new LiveHelpTokenError('Live Help is currently unavailable.', res.status);
  }
  const data = await res.json();
  if (!data?.token) {
    throw new LiveHelpTokenError('Live Help is currently unavailable.', res.status);
  }
  return { token: data.token, model: data.model || DEFAULT_LIVE_MODEL };
}

export type GeminiLiveStatus = 'IDLE' | 'CONNECTING' | 'LISTENING' | 'SPEAKING' | 'ERROR';

/**
 * Machine-readable reason for the last failure, so the UI can show localized,
 * actionable guidance instead of a raw browser exception string.
 */
export type GeminiLiveErrorCode =
  | 'MIC_PERMISSION_DENIED'
  | 'MIC_UNAVAILABLE'
  | 'UNSUPPORTED'
  | 'RATE_LIMITED'
  | 'CONNECTION_FAILED'
  | 'SESSION_CLOSED'
  | null;

export interface UseGeminiLiveReturn {
  status: GeminiLiveStatus;
  /** Raw (English, technical) error message — for logs / fallback display. */
  error: string | null;
  errorCode: GeminiLiveErrorCode;
  /**
   * Start a new session. Call this synchronously from a click/tap handler:
   * the AudioContexts and the microphone request are started before the first
   * `await`, which is what Safari requires to unlock audio.
   */
  connect: () => Promise<void>;
  disconnect: () => void;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  userTranscript: string;
  modelTranscript: string;
  audioContext: AudioContext | null;
  analyserNode: AnalyserNode | null;
}

export interface UseGeminiLiveOptions {
  /** System instruction for the Live session. Default: the portal instruction. */
  systemInstruction?: string;
  /** Mints the ephemeral token. Default: public `/api/live-help/token`. */
  tokenFetcher?: () => Promise<{ token: string; model: string }>;
}

/** Error carrying a GeminiLiveErrorCode through the connect() try/catch. */
class LiveConnectError extends Error {
  code: Exclude<GeminiLiveErrorCode, null>;
  constructor(code: Exclude<GeminiLiveErrorCode, null>, message: string) {
    super(message);
    this.code = code;
  }
}

const MIC_DENIED_NAMES = new Set(['NotAllowedError', 'PermissionDeniedError', 'SecurityError']);

export function classifyMicError(err: unknown): 'MIC_PERMISSION_DENIED' | 'MIC_UNAVAILABLE' {
  const name = (err as { name?: string } | null)?.name ?? '';
  const message = (err as { message?: string } | null)?.message ?? '';
  if (MIC_DENIED_NAMES.has(name) || /permission denied|not allowed/i.test(message)) {
    return 'MIC_PERMISSION_DENIED';
  }
  // NotFoundError, NotReadableError (mic busy in another app), OverconstrainedError, AbortError...
  return 'MIC_UNAVAILABLE';
}

/** HTTP status from a fetch-based (LiveHelpTokenError) or axios-based fetcher error. */
function httpStatusOf(err: unknown): number | undefined {
  const e = err as { status?: unknown; response?: { status?: unknown } } | null;
  if (typeof e?.status === 'number') return e.status;
  if (typeof e?.response?.status === 'number') return e.response.status;
  return undefined;
}

function messageOf(err: unknown, fallback: string): string {
  const m = (err as { message?: unknown } | null)?.message;
  return typeof m === 'string' && m ? m : fallback;
}

type AudioContextCtor = typeof AudioContext;

function getAudioContextClass(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext ||
    null
  );
}

export const useGeminiLive = (options: UseGeminiLiveOptions = {}): UseGeminiLiveReturn => {
  // Kept in a ref so `connect` always uses the latest options without being
  // re-created.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [status, setStatus] = useState<GeminiLiveStatus>('IDLE');
  // Mirror of `status` readable synchronously from callbacks (avoids the stale
  // closure that made the old `connect` bail out early).
  const statusRef = useRef<GeminiLiveStatus>('IDLE');
  const updateStatus = useCallback((next: GeminiLiveStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<GeminiLiveErrorCode>(null);
  const [userTranscript, setUserTranscript] = useState<string>('');
  const [modelTranscript, setModelTranscript] = useState<string>('');
  // State (not just a ref) so the modal re-renders when the visualizer source appears.
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  // Monotonic session id. Every async step and WebSocket callback compares its
  // captured id with the current one, so a late `onclose`/`onmessage` from a
  // previous session can never mutate the state of the current one.
  const connectionIdRef = useRef(0);

  const sessionRef = useRef<Session | null>(null);
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const scriptProcessorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const inputAnalyserRef = useRef<AnalyserNode | null>(null);
  const outputNodeRef = useRef<GainNode | null>(null);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const nextStartTimeRef = useRef<number>(0);

  /**
   * Create fresh AudioContexts and start resuming them. MUST run synchronously
   * inside the user gesture (no `await` before it): Safari only lets an
   * AudioContext start from a click/tap, and contexts created on mount stay
   * `suspended`. A fresh pair per session also discards any playback schedule
   * left over from the previous session.
   */
  const createAudioContexts = useCallback((): Promise<unknown> => {
    const Ctor = getAudioContextClass();
    if (!Ctor) return Promise.resolve();

    const input = new Ctor({ sampleRate: 16000 });
    const output = new Ctor({ sampleRate: 24000 });
    const gain = output.createGain();
    gain.connect(output.destination);

    inputAudioContextRef.current = input;
    outputAudioContextRef.current = output;
    outputNodeRef.current = gain;
    nextStartTimeRef.current = 0;

    // Kick off resume() immediately (still inside the gesture); await later.
    return Promise.all([
      input.state === 'suspended' ? input.resume() : undefined,
      output.state === 'suspended' ? output.resume() : undefined,
    ]).catch(err => {
      console.warn('[Gemini Live] AudioContext resume failed:', err);
    });
  }, []);

  const closeAudioContexts = useCallback(() => {
    for (const ref of [inputAudioContextRef, outputAudioContextRef]) {
      const ctx = ref.current;
      ref.current = null;
      if (ctx && ctx.state !== 'closed') {
        // Browsers cap concurrent AudioContexts (Safari especially); always close.
        ctx.close().catch(() => undefined);
      }
    }
    outputNodeRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    const processor = scriptProcessorNodeRef.current;
    scriptProcessorNodeRef.current = null;
    if (processor) {
      processor.onaudioprocess = null;
      try {
        processor.disconnect();
      } catch {
        // already disconnected
      }
    }

    const source = sourceNodeRef.current;
    sourceNodeRef.current = null;
    if (source) {
      try {
        source.disconnect();
      } catch {
        // already disconnected
      }
    }

    const analyser = inputAnalyserRef.current;
    inputAnalyserRef.current = null;
    if (analyser) {
      try {
        analyser.disconnect();
      } catch {
        // already disconnected
      }
    }
    setAnalyserNode(null);

    const stream = mediaStreamRef.current;
    mediaStreamRef.current = null;
    stream?.getTracks().forEach(track => track.stop());
  }, []);

  const stopPlayback = useCallback(() => {
    sourcesRef.current.forEach(source => {
      try {
        source.stop();
      } catch {
        // already stopped
      }
    });
    sourcesRef.current.clear();
    nextStartTimeRef.current = 0;
  }, []);

  /** Close the WebSocket (if any) without touching React state. */
  const closeSession = useCallback(() => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) {
      try {
        session.close();
      } catch (e) {
        console.warn('[Gemini Live] Error closing session:', e);
      }
    }
  }, []);

  /** Release mic, playback, socket and audio contexts. */
  const teardown = useCallback(() => {
    stopRecording();
    stopPlayback();
    closeSession();
    closeAudioContexts();
  }, [stopRecording, stopPlayback, closeSession, closeAudioContexts]);

  const disconnect = useCallback(() => {
    // Invalidate every in-flight callback of the closing session first.
    connectionIdRef.current += 1;
    teardown();
    updateStatus('IDLE');
    setUserTranscript('');
    setModelTranscript('');
    setError(null);
    setErrorCode(null);
  }, [teardown, updateStatus]);

  /** Wire an acquired mic stream into the Web Audio graph and stream PCM to Gemini. */
  const attachStream = useCallback((stream: MediaStream) => {
    const ctx = inputAudioContextRef.current;
    if (!ctx || sourceNodeRef.current) return;

    const source = ctx.createMediaStreamSource(stream);
    sourceNodeRef.current = source;

    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    inputAnalyserRef.current = analyser;
    setAnalyserNode(analyser);

    // 4096 frames @16 kHz ≈ 256 ms per chunk.
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    scriptProcessorNodeRef.current = processor;
    const sampleRate = ctx.sampleRate;
    processor.onaudioprocess = event => {
      const session = sessionRef.current;
      if (!session) return;
      try {
        session.sendRealtimeInput({ media: createBlob(event.inputBuffer.getChannelData(0), sampleRate) });
      } catch (e) {
        // "WebSocket is already in CLOSING or CLOSED state" while tearing down.
        console.debug('[Gemini Live] Error sending audio (likely session closed):', e);
      }
    };
    source.connect(processor);
    // ScriptProcessor only runs while connected to a destination; its output
    // buffer is left zeroed, so nothing is played back.
    processor.connect(ctx.destination);
  }, []);

  /**
   * Re-acquire the microphone for the current session (e.g. after
   * `stopRecording()`). Not needed for normal use: `connect()` does this.
   */
  const startRecording = useCallback(async () => {
    if (!sessionRef.current || !inputAudioContextRef.current) return;
    const myId = connectionIdRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      if (connectionIdRef.current !== myId) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      mediaStreamRef.current = stream;
      attachStream(stream);
    } catch (err) {
      if (connectionIdRef.current !== myId) return;
      console.error('[Gemini Live] Error starting recording:', err);
      setErrorCode(classifyMicError(err));
      setError(messageOf(err, 'Unable to access microphone.'));
    }
  }, [attachStream]);

  const connect = useCallback(async () => {
    // Ignore double clicks while a handshake is already in flight.
    if (statusRef.current === 'CONNECTING') return;

    // Tear down anything left from a previous session (socket, tracks, contexts).
    teardown();
    const myId = ++connectionIdRef.current;
    const isCurrent = () => connectionIdRef.current === myId;

    updateStatus('CONNECTING');
    setError(null);
    setErrorCode(null);
    setUserTranscript('');
    setModelTranscript('');

    let handshakeTimer: ReturnType<typeof setTimeout> | undefined;

    try {
      // ---- 1. Synchronous part: still inside the user's click/tap ----------
      if (!navigator.mediaDevices?.getUserMedia || !getAudioContextClass()) {
        // Also the case on plain http:// (mediaDevices is undefined outside
        // secure contexts).
        throw new LiveConnectError('UNSUPPORTED', 'Microphone access is not supported in this browser.');
      }
      const resumed = createAudioContexts();
      const micRequest = navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });

      // ---- 2. Microphone first: a denial must not burn a 1-use token -------
      let stream: MediaStream;
      try {
        stream = await micRequest;
      } catch (micErr) {
        const code = classifyMicError(micErr);
        throw new LiveConnectError(
          code,
          code === 'MIC_PERMISSION_DENIED'
            ? 'Microphone access was blocked.'
            : messageOf(micErr, 'Unable to access microphone.')
        );
      }
      if (!isCurrent()) {
        // User closed the modal while the permission prompt was open.
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      mediaStreamRef.current = stream;
      await resumed;
      if (!isCurrent()) return;

      // ---- 3. Mint the single-use ephemeral token -------------------------
      let token: string;
      let model: string;
      try {
        ({ token, model } = await (optionsRef.current.tokenFetcher ?? fetchEphemeralToken)());
      } catch (tokenErr) {
        if (httpStatusOf(tokenErr) === 429) {
          throw new LiveConnectError('RATE_LIMITED', 'Too many Live Help sessions. Please wait a few minutes.');
        }
        throw new LiveConnectError('CONNECTION_FAILED', messageOf(tokenErr, 'Live Help is currently unavailable.'));
      }
      if (!isCurrent()) return;

      // ---- 4. Open the Live WebSocket -------------------------------------
      const client = new GoogleGenAI({
        apiKey: token,
        // Ephemeral tokens require the v1alpha API surface.
        httpOptions: { apiVersion: 'v1alpha' },
      });

      const sessionPromise = client.live.connect({
        model,
        callbacks: {
          onopen: () => {
            if (!isCurrent()) return;
            console.log('[Gemini Live] session opened');
          },
          onmessage: async (message: LiveServerMessage) => {
            if (!isCurrent()) return;
            const content = message.serverContent;
            if (!content) return;

            if (content.interrupted) {
              stopPlayback();
              updateStatus('LISTENING');
            }

            for (const part of content.modelTurn?.parts ?? []) {
              // Native-audio models can emit "thought" text parts; never show those.
              if (part.text && !part.thought) {
                const text = part.text;
                setModelTranscript(prev => prev + text);
              }

              const data = part.inlineData?.data;
              const outCtx = outputAudioContextRef.current;
              const outNode = outputNodeRef.current;
              if (!data || !outCtx || !outNode) continue;

              updateStatus('SPEAKING');
              const audioBuffer = await decodeAudioData(decode(data), outCtx, 24000, 1);
              // The session (and this context) may have ended while decoding.
              if (!isCurrent() || outputAudioContextRef.current !== outCtx) return;

              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, outCtx.currentTime);
              const source = outCtx.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(outNode);
              source.addEventListener('ended', () => {
                sourcesRef.current.delete(source);
                if (isCurrent() && sourcesRef.current.size === 0 && statusRef.current === 'SPEAKING') {
                  updateStatus('LISTENING');
                }
              });
              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += audioBuffer.duration;
              sourcesRef.current.add(source);
            }
          },
          onerror: (e: ErrorEvent) => {
            if (!isCurrent()) return;
            console.error('[Gemini Live] error:', e);
            connectionIdRef.current += 1;
            teardown();
            setErrorCode('CONNECTION_FAILED');
            setError(e?.message || 'An error occurred with Gemini Live');
            updateStatus('ERROR');
          },
          onclose: (e: CloseEvent) => {
            if (!isCurrent()) return;
            console.log('[Gemini Live] session closed by server:', e?.code, e?.reason);
            connectionIdRef.current += 1;
            teardown();
            if (e?.code && e.code !== 1000) {
              // Abnormal close (bad/expired token, model constraint mismatch,
              // network drop...). Surface it instead of silently going IDLE.
              setErrorCode('CONNECTION_FAILED');
              setError(e.reason || `Live session closed (code ${e.code}).`);
              updateStatus('ERROR');
            } else {
              // Normal server-side end (session time limit, GoAway).
              setErrorCode('SESSION_CLOSED');
              setError('The Live session ended.');
              updateStatus('IDLE');
            }
          },
        },
        config: {
          responseModalities: [Modality.AUDIO],
          systemInstruction: optionsRef.current.systemInstruction ?? GEMINI_SYSTEM_INSTRUCTION,
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } },
          },
        },
      });

      const handshakeTimeout = new Promise<never>((_, reject) => {
        handshakeTimer = setTimeout(
          () => reject(new LiveConnectError('CONNECTION_FAILED', 'Timed out connecting to Live Help.')),
          LIVE_CONNECT_TIMEOUT_MS
        );
      });

      let session: Session;
      try {
        session = await Promise.race([sessionPromise, handshakeTimeout]);
      } catch (err) {
        // If the socket opens after we gave up, close it immediately.
        sessionPromise.then(s => s.close()).catch(() => undefined);
        throw err;
      }

      if (!isCurrent()) {
        // Superseded (closed/restarted) while the handshake was in flight —
        // or onclose/onerror already fired during setup.
        try {
          session.close();
        } catch {
          // ignore
        }
        return;
      }

      sessionRef.current = session;
      updateStatus('LISTENING');
      attachStream(stream);
    } catch (err) {
      if (!isCurrent()) return;
      console.error('[Gemini Live] failed to connect:', err);
      connectionIdRef.current += 1;
      teardown();
      setErrorCode(err instanceof LiveConnectError ? err.code : 'CONNECTION_FAILED');
      setError(messageOf(err, 'Failed to connect'));
      updateStatus('ERROR');
    } finally {
      if (handshakeTimer) clearTimeout(handshakeTimer);
    }
  }, [teardown, createAudioContexts, updateStatus, stopPlayback, attachStream]);

  // Release the mic and socket when the component unmounts. `teardown` is
  // stable (all deps are stable), so this only runs on unmount.
  useEffect(() => {
    return () => {
      connectionIdRef.current += 1;
      teardown();
    };
  }, [teardown]);

  return {
    status,
    error,
    errorCode,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    userTranscript,
    modelTranscript,
    audioContext: inputAudioContextRef.current,
    analyserNode,
  };
};
